import React, { Suspense, useCallback } from 'react';
import LoadingSpinner from 'components/LoadingSpinner';
import { getModalComponent, isModalLazy } from './ModalRegistry';
import { getModalConfig, validateModalData } from '../../config/modalConfigs';

/**
 * Loading fallback component for lazy-loaded modals
 */
const ModalLoadingFallback = () => <LoadingSpinner />;

/**
 * ModalContainer - A wrapper component that handles modal rendering, validation, and lifecycle
 *
 * This component serves as a bridge between the modal management system and the actual modal components.
 * It handles lazy loading, validation, error boundaries, and provides a consistent API.
 *
 * @param {Object} props - Component props
 * @param {string} props.modalId - Unique modal identifier
 * @param {Object} props.modalState - Current modal state
 * @param {boolean} props.modalState.isOpen - Whether the modal is open
 * @param {Object} [props.modalState.data] - Modal data
 * @param {boolean} [props.modalState.loading] - Loading state
 * @param {Object} [props.modalState.errors] - Error state
 * @param {Object} [props.modalState.config] - Modal configuration
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onSubmit - Function to handle modal submission
 * @param {Function} props.onUpdate - Function to update modal state
 * @param {Object|null} [props.userInfo=null] - Current user information
 * @param {Array} [props.roles=[]] - Available user roles
 * @param {Object} [props.additionalProps={}] - Additional props to pass to modal
 * @returns {JSX.Element|null} Rendered modal component or null
 */
const ModalContainer = ({
  modalId,
  modalState,
  onClose,
  onSubmit,
  onUpdate,
  userInfo = null,
  roles = [],
  additionalProps = {},
}) => {
  // Get modal configuration and component
  const config = getModalConfig(modalId);
  const ModalComponent = getModalComponent(modalId);

  // If no config or component found, don't render anything
  if (!config || !ModalComponent || !modalState?.isOpen) {
    return null;
  }

  /**
   * Handle modal close with cleanup
   */
  const handleClose = useCallback(() => {
    if (modalState.loading) {
      // Prevent closing while loading
      return;
    }
    onClose(modalId);
  }, [modalId, modalState.loading, onClose]);

  /**
   * Handle modal submission with validation
   */
  const handleSubmit = useCallback(
    async (formData) => {
      try {
        // Validate form data if validation function exists
        const errors = validateModalData(modalId, formData);

        if (Object.keys(errors).length > 0) {
          // Update modal with validation errors
          onUpdate(modalId, { errors });
          return;
        }

        // Clear any existing errors
        onUpdate(modalId, { errors: {}, loading: true });

        // Call the submit handler
        await onSubmit(modalId, formData);

        // Close modal on successful submission
        handleClose();
      } catch (error) {
        // Handle submission error - extract detailed error message
        let errorMessage = 'An error occurred while processing your request';

        // Check for API error response structure
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.detail) {
          errorMessage = error.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }

        onUpdate(modalId, {
          errors: { general: errorMessage },
          loading: false,
        });
      }
    },
    [modalId, onSubmit, onUpdate, handleClose]
  );

  /**
   * Handle form data updates
   */
  const handleFormUpdate = useCallback(
    (updates) => {
      onUpdate(modalId, {
        data: { ...modalState.data, ...updates },
        // Clear errors when user makes changes
        errors: {},
      });
    },
    [modalId, modalState.data, onUpdate]
  );

  // Prepare props to pass to the modal component
  const modalProps = {
    // Standard modal props
    open: modalState.isOpen,
    onClose: handleClose,
    onSubmit: handleSubmit,
    loading: modalState.loading,
    errors: modalState.errors,

    // Configuration-based props
    title: config.title,
    submitText: config.submitText,
    cancelText: config.cancelText,

    // Data and state
    data: modalState.data,
    onDataUpdate: handleFormUpdate,

    // External dependencies
    userInfo,
    roles,

    // Modal-specific props from config
    ...config.props,

    // Additional props passed from parent
    ...additionalProps,
  };

  // Handle specific modal types
  switch (config.type) {
    case 'confirmation':
      modalProps.onConfirm = handleSubmit;
      modalProps.message = config.props?.message;
      break;

    case 'form':
      // Form-specific props are already handled above
      break;

    default:
      // Default behavior
      break;
  }

  // Render with or without Suspense based on lazy loading
  if (isModalLazy(modalId)) {
    return (
      <Suspense fallback={<ModalLoadingFallback />}>
        <ModalComponent {...modalProps} />
      </Suspense>
    );
  }

  return <ModalComponent {...modalProps} />;
};

export default ModalContainer;
